import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ITipoTurno, TIPOS_TURNOS, Turnos, TurnosVariosEmpleados } from "../../../models/personal/turnos.model";
import { EmployeesListModel } from "../employees-list.model";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TurnosService } from "../../../services/employees/turnos.service";
import { TurnosComponent } from "./turnos.component";
import moment from 'moment';
import { Option } from "../../../shared/models/option.model";

interface IFechasDescanso {
  fecha: Date;
  cadena: string;
}

@Component({
    selector: "form-turnos",
    templateUrl: "./turnos-form.component.html",
    styleUrls: [],
    standalone: false
})
export class TurnosFormComponent implements OnInit {
  // #region Propiedades del componente
  displayedColumns: string[] = ['Fecha', 'acciones'];
  form: UntypedFormGroup = null;
  listaDiasSemana: any[] = [
    { etiqueta: "Lunes", valor: 1 },
    { etiqueta: "Martes", valor: 2 },
    { etiqueta: "Miércoles", valor: 3 },
    { etiqueta: "Jueves", valor: 4 },
    { etiqueta: "Viernes", valor: 5 },
    { etiqueta: "Sábado", valor: 6 },
    { etiqueta: "Domingo", valor: 0 },
  ];
  listaTiposTurnos: ITipoTurno[] = TIPOS_TURNOS;
  horasOptions: Option[] = [
    { value: '06:00-14:00', label: '06:00 – 14:00' },
    { value: '14:00-22:00', label: '14:00 – 22:00' },
    { value: '22:00-06:00', label: '22:00 – 06:00' },
    { value: '09:00-17:00', label: '09:00 – 17:00' },
    { value: '08:00-16:00', label: '08:00 – 16:00' },
    { value: '09:00-14:00 17:00-20:00', label: '09:00–14:00 / 17:00–20:00' },
    { value: 'especificar', label: 'Especificar…' },
  ];
  empleados: EmployeesListModel[] = [];
  fechas: IFechasDescanso[] = [];
  turno: Turnos;
  turnoVariosEmpleados: TurnosVariosEmpleados = this.TurnosService.newTurnoVariosEmpleados();
  esEdicion: boolean = false;
  // #endregion

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<TurnosComponent>,
    protected snackBar: MatSnackBar,
    protected TurnosService: TurnosService
  ) { }

  // #region Métodos del componente

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.empleados = this.data.empleados;

    if (this.data.turno != undefined && this.data.turno.id > 0) {
      this.turno = this.data.turno;
      this.esEdicion = true;
    } else {
      this.turnoVariosEmpleados = this.TurnosService.newTurnoVariosEmpleados();
      this.esEdicion = false;
    }

    this.inicializarFormulario();
  }

  onSubmit(): void {
    if (this.form.valid) {

      if (this.esEdicion) {
        this.turno.nombre = this.form.value.nombre;
        this.turno.descripcion = this.form.value.descripcion;
        this.turno.tipo = this.form.value.tipo;
        this.turno.inicio = this.form.value.inicio || null;
        this.turno.fin = this.form.value.fin || null;
        this.turno.horas = this.form.value.horas === 'especificar' ? this.form.value.horasPersonalizado : this.form.value.horas;
        this.turno.diasALaSemana = this.form.value.diasALaSemana != null && this.form.value.diasALaSemana.length > 0 ? this.form.value.diasALaSemana.join(',') : '';
        this.turno.diasDescanso = this.fechas != null && this.fechas.length > 0 ? this.fechas.map(f => f.cadena).join(',') : '';
        this.turno.creacion = new Date();

        // Actualización de un turno existente
        this.TurnosService.saveTurno(this.turno)
          .subscribe({
            next: (resultado: Turnos) => {
              if (resultado) {
                this.dialogRef.close();
              }
            }
          });
      } else {
        // Alta de nuevos turnos para varios empleados
        this.turnoVariosEmpleados.nombre = this.form.value.nombre;
        this.turnoVariosEmpleados.descripcion = this.form.value.descripcion;
        this.turnoVariosEmpleados.tipo = this.form.value.tipo;
        this.turnoVariosEmpleados.inicio = this.form.value.inicio || null;
        this.turnoVariosEmpleados.fin = this.form.value.fin || null;
        this.turnoVariosEmpleados.horas = this.form.value.horas === 'especificar' ? this.form.value.horasPersonalizado : this.form.value.horas;
        this.turnoVariosEmpleados.diasALaSemana = this.form.value.diasALaSemana != null && this.form.value.diasALaSemana.length > 0 ? this.form.value.diasALaSemana.join(',') : '';
        this.turnoVariosEmpleados.diasDescanso = this.fechas != null && this.fechas.length > 0 ? this.fechas.map(f => f.cadena).join(',') : '';
        this.turnoVariosEmpleados.creacion = new Date();

        this.turnoVariosEmpleados.employees_Ids = this.form.value.employees_Ids;
        this.TurnosService.guardarTurnoVariosEmpleados(this.turnoVariosEmpleados)
          .subscribe({
            next: (resultado: Turnos[]) => {
              if (resultado) {
                this.dialogRef.close();
              }
            }
          });
      }
    }
  }

  // #endregion

  // #region Métodos relacionados con las fechas

  /**
   * Agrega una fecha de descanso a la lista de fechas de descanso.
   * Obtiene la fecha del formulario y la agrega a la lista de fechas de descanso.
   */
  agregarFecha(): void {
    if (this.form.value.fechaDescanso != null) {
      const cadenaFecha: string = moment(this.form.value.fechaDescanso).format('DD/MM/YYYY');

      if (this.fechas.find((f) => f.cadena == cadenaFecha) != undefined) {
        this.snackBar.open("La fecha ya ha sido agregada", "Cerrar", {
          duration: 3000,
        });
        return;
      }
      this.fechas.push({
        cadena: cadenaFecha,
        fecha: this.form.value.fechaDescanso
      });

      // Ordenación de las fechas
      this.fechas.sort((a, b) => {
        if (a.fecha < b.fecha) {
          return -1;
        }
        if (a.fecha > b.fecha) {
          return 1;
        }
        return 0;
      });

      this.form.controls.fechaDescanso.setValue(null);
    }
  }

  /**
   * Elimina una fecha de descanso de la lista de fechas de descanso.
   * @param fecha Fecha de descanso a eliminar.
   */
  eliminarFecha(fecha: IFechasDescanso): void {
    const cadenaFecha: string = moment(fecha.fecha).format('DD/MM/YYYY');
    this.fechas = this.fechas.filter((f) => f.cadena != cadenaFecha);
  }

  /**
   * Comprueba las condiciones que se deben cumplir para que se muestre la tabla de fechas de descanso y el formulario para añadirlas.
   * Debe cumplir las siguientes condiciones:
   * - El turno debe ser de tipo 3 (personalizado)
   * - El turno debe tener un inicio y un fin
   * @returns Devuelve true si hay que activar la tabla de fechas de descanso
   */
  mostrarTablaFechasDescanso(): boolean {
    return this.form.value.tipo == 3 && this.form.value.inicio != null && this.form.value.fin != null;
  }

  // #endregion

  // #region Métodos privados del componente

  private inicializarFormulario() {
    let diasSemana: number[] = [];
    let formGroupConfig: any;

    let horasInicial = this.esEdicion ? this.turno.horas : this.turnoVariosEmpleados.horas;
    let horasPersonalizado = '';
    if (horasInicial && !['06:00-14:00', '14:00-22:00', '22:00-06:00', '09:00-17:00', '08:00-16:00', '09:00-14:00 17:00-20:00'].includes(horasInicial)) {
      horasPersonalizado = horasInicial;
      horasInicial = 'especificar';
    }

    if (this.esEdicion) {
      if (this.turno.diasALaSemana) {
        diasSemana = this.turno.diasALaSemana.split(',').map((d) => parseInt(d, 10));
      }
      if (this.turno.diasDescanso != null && this.turno.diasDescanso != "") {
        this.fechas = this.turno.diasDescanso.split(",").map((f) => {
          return {
            cadena: f,
            fecha: moment(f, 'DD/MM/YYYY').toDate()
          }
        });
      }

      formGroupConfig = {
        nombre: new UntypedFormControl(this.turno.nombre, [Validators.required]),
        descripcion: new UntypedFormControl(this.turno.descripcion),
        tipo: new UntypedFormControl(this.turno.tipo, [Validators.required]),
        inicio: new UntypedFormControl(this.turno.inicio || null),
        fin: new UntypedFormControl(this.turno.fin || null),
        diasALaSemana: new UntypedFormControl(diasSemana),
        horas: new UntypedFormControl(horasInicial),
        horasPersonalizado: new UntypedFormControl(horasPersonalizado),
        fechaDescanso: new UntypedFormControl(null),
      };

    } else {
      if (this.turnoVariosEmpleados.diasALaSemana) {
        diasSemana = this.turnoVariosEmpleados.diasALaSemana.split(',').map((d) => parseInt(d, 10));
      }
      if (this.turnoVariosEmpleados.diasDescanso != null && this.turnoVariosEmpleados.diasDescanso != "") {
        this.fechas = this.turnoVariosEmpleados.diasDescanso.split(",").map((f) => {
          return {
            cadena: f,
            fecha: moment(f, 'DD/MM/YYYY').toDate()
          }
        });
      }

      formGroupConfig = {
        nombre: new UntypedFormControl(this.turnoVariosEmpleados.nombre, [Validators.required]),
        descripcion: new UntypedFormControl(this.turnoVariosEmpleados.descripcion),
        tipo: new UntypedFormControl(this.turnoVariosEmpleados.tipo, [Validators.required]),
        inicio: new UntypedFormControl(this.turnoVariosEmpleados.inicio || null),
        fin: new UntypedFormControl(this.turnoVariosEmpleados.fin || null),
        diasALaSemana: new UntypedFormControl(diasSemana),
        horas: new UntypedFormControl(horasInicial),
        horasPersonalizado: new UntypedFormControl(horasPersonalizado),
        fechaDescanso: new UntypedFormControl(null),
        employees_Ids: new UntypedFormControl(this.turnoVariosEmpleados.employees_Ids || [], [Validators.required]),
      };
    } 

    this.form = new UntypedFormGroup(formGroupConfig);
  }

  /**
   * Devuelve el nombre del empleado asociado al turno (para edición).
   * Se usa any para aceptar tanto Turnos como TurnosVariosEmpleados.
   */
  getNombreEmpleado(turno: any): string {
    const id = turno.employees_Ids ? turno.employees_Ids[0] : turno.employees_Id;
    const empleado = this.empleados.find(e => e.uid === id);
    return empleado ? empleado.name : '';
  }

  // #endregion


  public trackByIndex(i: number) { return i; }

}
